import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getMonsterImgPath } from '../utils/monster';
import BackButton from './BackButton';
import Segmented from './Segmented';
import SiegeRateTrend from './SiegeRateTrend';
import { minForPeriod } from '../utils/period';

/**
 * **통계 탭의 공덱 쪽** — 많이 쓴 공덱, 그리고 길드원이 많이 쓴 공덱.
 *
 * 방덱 쪽(`PopularDefense`)은 데스크톱·모바일이 따로 있는데 여기는 한 벌로 둔다. 목록 한
 * 줄에 몹 셋과 숫자 몇 개뿐이라 화면 크기에 따라 값만 줄이면 되고, 두 벌로 나누면 고칠 때
 * 마다 같은 자리를 두 번 만져야 한다.
 *
 * **차림은 방덱 쪽과 똑같이 맞춘다** — 큰 그라데이션 제목, 그 아래 한 줄 설명, 알약 거르개,
 * 그리고 흰 판 위의 목록. 통계 탭 안에서 화면마다 다르게 생기면 같은 자료로 보이지 않는다.
 *
 * 숫자는 모두 `/api/posts/popular-attacks` 에서 온다. 승/패는 **공격한 쪽 기준**이라
 * 뒤집을 일이 없다 — 뚫었으면 승이다.
 */

interface AttackDeck {
    attackTeam: any[];
    totalMatches: number;
    wins: number;
    losses: number;
    winRate: number;
}

interface Member {
    wizardId: number;
    wizardName: string;
}

interface Props {
    monsterMapping: Record<string, any>;
    isMobile: boolean;
    /** guild = 길드 전체가 많이 쓴 공덱 · member = 고른 길드원이 많이 쓴 공덱 top 10 */
    mode: 'guild' | 'member';
    /** 통계 탭에서 카드를 눌러 들어왔을 때 — 머리 판 왼쪽 위에 [← 뒤로가기] 를 놓는다 */
    onBack?: () => void;
    /** 길드원 모드에서 처음 고를 사람(게임 닉네임) — 프로필 창의 [길드원 통계] 가 내 이름을 준다. 없으면 명단 맨 앞 */
    initialName?: string;
}

/** 길드원 모드에서 몇 개까지 보여 줄지 */
const MEMBER_TOP = 10;
/** 길드 전체에서는 이 횟수 미만으로 쓴 덱은 뺀다 — 한두 번 써 본 것까지 세면 수백 줄이 된다 */
const GUILD_MIN = 5;

const panel: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 24,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
    width: '100%',
    boxSizing: 'border-box'
};

const AttackStats: React.FC<Props> = ({ monsterMapping, isMobile, mode, onBack, initialName }) => {
    const [seasons, setSeasons] = useState<number[]>([]);
    const [season, setSeason] = useState<number | null>(null);
    const [decks, setDecks] = useState<AttackDeck[]>([]);
    const [loading, setLoading] = useState(true);

    const [members, setMembers] = useState<Member[]>([]);
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const [picked, setPicked] = useState<number | null>(null);

    useEffect(() => {
        fetch('/api/posts/seasons')
            .then((r) => r.json())
            .then((d) => { if (Array.isArray(d?.seasons)) setSeasons(d.seasons); })
            .catch(() => null);
    }, []);

    // 길드원 모드일 때만 명단과 얼굴을 받는다
    useEffect(() => {
        if (mode !== 'member') return;
        let alive = true;
        Promise.all([
            fetch('/api/siege-battles/members?own=true').then((r) => r.json()).catch(() => null),
            fetch('/api/siege-battles/avatars').then((r) => r.json()).catch(() => null),
            fetch('/api/auth/me', { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
        ]).then(([m, a, me]) => {
            if (!alive) return;
            /** 내 것은 **맨 앞에** (사용자 지시 2026-10-10). 게임 아이디로 짚고, 없으면 닉네임으로 */
            const myId = Number(me?.wizardId) || null;
            const isMe = (x: Member) => (myId ? x.wizardId === myId : !!me?.nickname && x.wizardName === me.nickname);
            const list: Member[] = (m?.members || [])
                .filter((x: any) => Number.isFinite(Number(x?.wizardId)) && x?.wizardName)
                .map((x: any) => ({ wizardId: Number(x.wizardId), wizardName: String(x.wizardName) }))
                .sort((x: Member, y: Member) => (Number(isMe(y)) - Number(isMe(x))) || x.wizardName.localeCompare(y.wizardName, 'ko'));
            setMembers(list);
            setAvatars(a?.avatars || {});
            const mine = initialName ? list.find((x) => x.wizardName === initialName) : undefined;
            setPicked((cur) => (cur ?? (mine?.wizardId ?? list[0]?.wizardId ?? null)));
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    /**
     * 길드원 통계의 **공덱 / 방덱** 고르개 (사용자 2026-10-27 — 처음엔 공덱 통계에 넣었다가 "공덱 통계에 방덱 없애고 길드원 통계에 넣어").
     * 방덱 = 그 길드원이 공격하며 **많이 만난 상대 방덱**(`popular-defenses?side=opp&wizardId=`, 공격 기록의 defenseTeam). 목록 모양은 공덱과 같고
     * 승/패·성공률도 **그 사람 공격 기준**으로 적는다 — 그 방덱을 뚫었으면 승. API 는 방덱 기준(막았으면 승)이라 여기서 뒤집는다.
     */
    const [side, setSide] = useState<'atk' | 'def'>('atk');
    const def = mode === 'member' && side === 'def';
    /** 기간 — 공성률 그래프 밑 스크롤바로 고른 첫 판부터 지금까지 (사용자 2026-10-27). since = 첫 판 시작일, recent = 판 수(문턱 줄이기용) */
    const [since, setSince] = useState<string | null>(null);
    const [recent, setRecent] = useState(0);
    /** 공덱 통계의 태성 — 5 = 5성이 낀 공덱 · 4 = 4성 이하만 (방덱 통계와 같은 거르개, 사용자 2026-10-27) */
    const [stars, setStars] = useState<5 | 4>(5);

    useEffect(() => {
        if (mode === 'member' && !picked) { setDecks([]); setLoading(false); return; }
        let alive = true;
        setLoading(true);
        const q = new URLSearchParams();
        q.set('minCount', mode === 'member' ? '1' : String(minForPeriod(GUILD_MIN, recent)));
        if (season) q.set('season', String(season));
        if (mode === 'member') { q.set('wizardId', String(picked)); q.set('limit', String(MEMBER_TOP)); }
        if (def) q.set('side', 'opp');
        if (since) q.set('since', since);
        if (mode === 'guild') q.set('stars', String(stars));
        fetch(`/api/posts/${def ? 'popular-defenses' : 'popular-attacks'}?${q.toString()}`)
            .then((r) => r.json())
            .then((d) => {
                if (!alive) return;
                if (!def) { setDecks(Array.isArray(d?.attacks) ? d.attacks : []); return; }
                setDecks((Array.isArray(d?.defenses) ? d.defenses : []).map((x: any) => ({
                    attackTeam: x.defenseTeam, totalMatches: x.totalMatches,
                    wins: x.losses, losses: x.wins, winRate: x.winRate   // 방덱이 진 것 = 우리가 뚫은 것
                })));
            })
            .catch(() => { if (alive) setDecks([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [mode, season, picked, def, since, stars]);

    const pickedName = useMemo(
        () => members.find((m) => m.wizardId === picked)?.wizardName || '',
        [members, picked]
    );

    /** 가로 칸 너비 — 머리 줄과 몸 줄이 같은 값을 써야 세로로 맞는다 */
    /**
     * **표는 화면 판단(`isMobile`)이 아니라 제 폭을 보고 좁힌다.**
     *
     * 폰에서도 데스크톱 판으로 그려지는 기기가 있다(사용자 화면 — 성공률 칸이 잘렸다).
     * 그때 데스크톱 칸 너비를 쓰면 줄이 판보다 넓어져 오른쪽이 잘린다. 칸 너비는 화면 종류가
     * 아니라 **실제 남은 폭**에 달린 문제라 여기서 재는 것이 맞다.
     */
    const listRef = useRef<HTMLDivElement>(null);
    const [outerW, setOuterW] = useState(0);
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        const read = () => setOuterW(el.clientWidth);
        read();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    /**
     * **좁은 차림인지는 화면 종류가 아니라 잰 폭으로 정한다.** 폰인데도 데스크톱 판으로
     * 그려지는 기기가 있어(사용자 화면) 여백까지 넉넉한 값이 들어가면 표가 판을 넘어 잘렸다.
     * 넘겨받은 `isMobile` 은 아직 재기 전(첫 그림)에만 쓴다.
     */
    const sm = outerW > 0 ? outerW < 560 : isMobile;
    const tight = sm;

    const W_RANK = tight ? 22 : 32;
    /**
     * 숫자는 **전투 / 승패** 두 칸으로 나눈다.
     *
     * 한 칸에 "199회 184승 15패" 를 다 적었더니 줄마다 같은 낱말이 세 번씩 되풀이돼 숫자보다
     * 글자가 먼저 보였다. 무엇을 세는 칸인지는 머리 줄이 한 번만 말해 주면 된다.
     */
    const W_COUNT = tight ? 32 : 54;
    const W_WL = tight ? 56 : 86;
    const W_RATE = tight ? 50 : 70;

    /**
     * 한 줄은 **표의 한 행**이다 — 카드로 따로 떼어 놓지 않는다.
     *
     * 줄마다 그림자와 테두리를 두르고 사이를 띄웠더니 같은 목록이 열 조각으로 흩어져 보였고,
     * 남는 가로 자리를 성공률 막대로 채웠더니 90~98% 가 다 비슷해 그저 길기만 했다. 한 판
     * 안에 얇은 선으로만 나누고, 숫자는 오른쪽에 칸을 맞춰 세운다.
     */
    const row = (d: AttackDeck, i: number) => {
        const top3 = i < 3;
        const mob = tight ? 28 : 34;
        return (
            <div
                key={i}
                style={{
                    display: 'flex', alignItems: 'center', gap: sm ? 8 : 12,
                    padding: sm ? '8px 10px' : '10px 14px',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(102, 126, 234, 0.10)',
                    transition: 'background 0.15s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
                <div style={{
                    width: W_RANK, flexShrink: 0, textAlign: 'center',
                    fontSize: tight ? 11 : 12, fontWeight: 800,
                    color: top3 ? '#667eea' : '#c3c7dd'
                }}>{i + 1}</div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    {d.attackTeam.map((m: any, k: number) => {
                        const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                        return (
                            <img loading="lazy"
                                key={k}
                                src={imgPath ? `/mobs/v2/${imgPath.replace(/\\/g, '/')}` : '/images/empty.png'}
                                alt={displayName}
                                title={displayName}
                                style={{
                                    width: mob, height: mob, borderRadius: 8, objectFit: 'contain',
                                    background: '#f8faff', border: '1px solid rgba(102, 126, 234, 0.15)'
                                }}
                            />
                        );
                    })}
                </div>

                {/* 빈 자리 — 숫자를 오른쪽에 붙여 줄끼리 세로로 맞춘다 */}
                <div style={{ flex: 1, minWidth: 0 }} />

                <div style={{
                    width: W_COUNT, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap',
                    fontSize: tight ? 11 : 13, fontWeight: 700, color: '#555'
                }}>{d.totalMatches}</div>

                <div style={{
                    width: W_WL, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap',
                    fontSize: tight ? 11 : 13, fontWeight: 600
                }}>
                    <span style={{ color: '#2196F3' }}>{d.wins}</span>
                    <span style={{ color: '#ccd', margin: '0 4px' }}>/</span>
                    <span style={{ color: '#f44336' }}>{d.losses}</span>
                </div>

                <div style={{
                    width: W_RATE, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap',
                    fontSize: tight ? 13 : 15, fontWeight: 800, color: '#667eea'
                }}>{d.winRate}%</div>
            </div>
        );
    };

    const title = mode === 'member' ? '길드원 통계' : '공덱 통계';
    const caption = mode === 'member'
        ? (def ? `점령전별 공성률과 많이 만난 방덱 ${MEMBER_TOP}개입니다` : `점령전별 공성률과 많이 쓴 공덱 ${MEMBER_TOP}개입니다`)
        : `점령전별 공성률과 ${minForPeriod(GUILD_MIN, recent)}회 이상 쓴 공덱입니다`;
    const empty = mode === 'member'
        ? `${pickedName ? `${pickedName} 님의 ` : ''}${def ? '만난 방덱' : '공덱'} 기록이 없습니다.`
        : `${minForPeriod(GUILD_MIN, recent)}회 이상 쓴 공덱이 없습니다.`;

    return (
        <div ref={listRef} style={{
            padding: sm ? 12 : 24,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            minHeight: '100vh'
        }}>
            <div style={{
                width: '100%', maxWidth: 1200, margin: '0 auto',
                padding: sm ? '16px 4px' : '40px 20px',
                display: 'flex', flexDirection: 'column', alignItems: 'center'
            }}>
                {/* 머리 — 방덱 쪽과 같은 차림 */}
                <div style={{
                    ...panel,
                    padding: sm ? '24px 16px 20px' : '40px 32px 32px',
                    marginBottom: sm ? 16 : 32,
                    position: 'relative'
                }}>
                    <h2 style={{
                        margin: '0 0 8px', fontWeight: 900, fontSize: sm ? 26 : 40, letterSpacing: -1,
                        background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
                    }}>{title}</h2>
                    <p style={{ margin: '0 0 20px', fontSize: sm ? 12 : 14, color: '#888', fontWeight: 500 }}>
                        {caption}
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: sm ? 8 : 16 }}>
                        {mode === 'member' && (
                            <Segmented
                                label="보기"
                                isMobile={sm}
                                value={side}
                                onChange={setSide}
                                options={[{ value: 'atk' as const, label: '공덱' }, { value: 'def' as const, label: '방덱' }]}
                            />
                        )}
                        {mode === 'guild' && (
                            <Segmented
                                label="태성"
                                isMobile={sm}
                                value={stars}
                                onChange={setStars}
                                options={[{ value: 5 as const, label: '5성' }, { value: 4 as const, label: '4성' }]}
                            />
                        )}
                        <Segmented
                            label="시즌"
                            isMobile={sm}
                            value={season}
                            onChange={setSeason}
                            options={[{ value: null as number | null, label: '전체' },
                                      ...seasons.map((s) => ({ value: s as number | null, label: `${s}` }))]}
                        />

                    </div>

                    {mode === 'member' && members.length > 0 && (
                        <div style={{
                            display: 'flex', gap: sm ? 4 : 6, flexWrap: 'wrap', justifyContent: 'center',
                            marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(102, 126, 234, 0.12)'
                        }}>
                            {members.map((m) => {
                                const on = picked === m.wizardId;
                                return (
                                    <button
                                        key={m.wizardId}
                                        onClick={() => setPicked(m.wizardId)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            padding: sm ? '2px 7px 2px 2px' : '4px 12px 4px 4px',
                                            borderRadius: 999, border: 'none',
                                            background: on ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(102, 126, 234, 0.08)',
                                            color: on ? '#fff' : '#555',
                                            fontSize: sm ? 11 : 13, fontWeight: 700, cursor: 'pointer',
                                            lineHeight: 1.4
                                        }}
                                    >
                                        <img loading="lazy"
                                            src={avatars[m.wizardName] || '/images/default-profile.png'}
                                            alt=""
                                            style={{
                                                width: sm ? 16 : 24, height: sm ? 16 : 24,
                                                borderRadius: '50%', objectFit: 'cover', background: '#fff'
                                            }}
                                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/images/default-profile.png'; }}
                                        />
                                        {m.wizardName}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {/*
                      뒤로가기는 **판 왼쪽 위 구석에 띄운다.** 제목이 가운데라 그 옆이 비어 있어
                      자리를 더 먹지 않는다. 흐름에 두면 단추 하나가 넓은 줄을 통째로 차지했다.
                    */}
                    {onBack && (
                        <div style={{ position: 'absolute', left: sm ? 14 : 24, top: sm ? 14 : 24 }}>
                            <BackButton onClick={onBack} />
                        </div>
                    )}
                </div>

                {/*
                  점령전별 공성률 — 목록 위에 따로 한 판. 어느 판에서 잘 뚫었는지는 덱 목록으로는
                  안 보인다. 길드원 쪽이면 그 사람 것만 센다.
                */}
                <SiegeRateTrend
                    wizardId={mode === 'member' ? picked : null}
                    season={season}
                    onPeriodChange={(s, n) => { setSince(s); setRecent(n); }}
                    isMobile={sm}
                />

                {/* 목록 */}
                <div style={{ ...panel, padding: sm ? 12 : 24, minHeight: 240 }}>
                    {loading ? (
                        <div style={{
                            display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200,
                            color: '#667eea', fontSize: 15, fontWeight: 600
                        }}>불러오는 중...</div>
                    ) : decks.length === 0 ? (
                        <div style={{ color: '#999', fontSize: 15, padding: 40 }}>{empty}</div>
                    ) : (
                        <div style={{
                            borderRadius: 16, overflow: 'hidden',
                            border: '1px solid rgba(102, 126, 234, 0.14)'
                        }}>
                            {/* 머리 줄 — 어느 숫자가 무엇인지 한 번만 적는다 */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: sm ? 8 : 12,
                                padding: sm ? '7px 10px' : '8px 14px',
                                background: 'rgba(102, 126, 234, 0.06)',
                                fontSize: sm ? 10 : 11, fontWeight: 800, color: '#8b90b5'
                            }}>
                                <span style={{ width: W_RANK, flexShrink: 0, textAlign: 'center' }}>#</span>
                                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>{def ? '만난 방덱' : '공덱'}</span>
                                <span style={{ width: W_COUNT, flexShrink: 0, textAlign: 'right' }}>{def ? '만남' : '전투'}</span>
                                <span style={{ width: W_WL, flexShrink: 0, textAlign: 'right' }}>승 / 패</span>
                                <span style={{ width: W_RATE, flexShrink: 0, textAlign: 'right' }}>공성률</span>
                            </div>
                            {decks.map(row)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttackStats;
